import React, { useState, useEffect } from 'react';

const AdminSystemSettings: React.FC = () => {
  const [values, setValues] = useState({
    maxFileSize: 50,
    maxRequestSize: 60,
    connectionTimeout: 300,
    maxConnections: 8192,
    acceptCount: 100,
    maxThreads: 200,
    minSpareThreads: 10,
    keepAliveTimeout: 300,
    maxKeepAliveRequests: 100,
    corePoolSize: 8,
    maxPoolSize: 16,
    queueCapacity: 100,
    keepAliveSeconds: 60
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading current settings
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, []);

  const handleInputChange = (field: string, value: number) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      // Validate values
      if (values.maxFileSize < 1 || values.maxFileSize > 1000) {
        setSaveMessage('Max file size must be between 1 and 1000 MB');
        return;
      }
      if (values.maxRequestSize < values.maxFileSize) {
        setSaveMessage('Max request size must be greater than or equal to max file size');
        return;
      }
      if (values.connectionTimeout < 30 || values.connectionTimeout > 600) {
        setSaveMessage('Connection timeout must be between 30 and 600 seconds');
        return;
      }

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      setSaveMessage('System settings saved successfully! Changes will take effect after server restart.');
      
      // Clear success message after 5 seconds
      setTimeout(() => setSaveMessage(''), 5000);

    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveMessage('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ 
        maxWidth: 800, 
        margin: '32px auto', 
        padding: '32px',
        textAlign: 'center'
      }}>
        <div>Loading system settings...</div>
      </div>
    );
  }

  return (
    <div style={{ 
      maxWidth: 1000, 
      margin: '32px auto', 
      padding: '32px',
      background: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid #e5e7eb'
    }}>
      {/* Header */}
      <div style={{ 
        marginBottom: '32px',
        paddingBottom: '24px',
        borderBottom: '2px solid #f3f4f6'
      }}>
        <h2 style={{ 
          margin: '0 0 8px 0', 
          color: '#1f2937',
          fontSize: '28px',
          fontWeight: '700',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            🔧 System Configuration
          </span>
        </h2>
        <p style={{ 
          margin: 0, 
          color: '#6b7280', 
          fontSize: '16px',
          lineHeight: '1.5'
        }}>
          Configure server settings, file upload limits, and performance parameters
        </p>
      </div>

      {/* File Upload Settings */}
      <div style={{ 
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '20px',
          gap: '12px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            📁
          </div>
          <h3 style={{ 
            margin: 0, 
            color: '#1e293b',
            fontSize: '20px',
            fontWeight: '600'
          }}>
            File Upload Configuration
          </h3>
        </div>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '24px',
          alignItems: 'start'
        }}>
          <div style={{
            background: '#ffffff',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '12px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Max File Size (MB)
            </label>
            <input
              type="number"
              min={1}
              max={1000}
              value={values.maxFileSize}
              onChange={e => handleInputChange('maxFileSize', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
            <div style={{ 
              fontSize: '13px', 
              color: '#6b7280', 
              marginTop: '8px'
            }}>
              Maximum size for individual file uploads
            </div>
          </div>
          
          <div style={{
            background: '#ffffff',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '12px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Max Request Size (MB)
            </label>
            <input
              type="number"
              min={1}
              max={1000}
              value={values.maxRequestSize}
              onChange={e => handleInputChange('maxRequestSize', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
            <div style={{ 
              fontSize: '13px', 
              color: '#6b7280', 
              marginTop: '8px'
            }}>
              Maximum size for entire HTTP requests
            </div>
          </div>
        </div>
      </div>

      {/* Server Configuration */}
      <div style={{ 
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
        borderRadius: '12px',
        border: '1px solid #d1fae5'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '20px',
          gap: '12px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            🖥️
          </div>
          <h3 style={{ 
            margin: 0, 
            color: '#1e293b',
            fontSize: '20px',
            fontWeight: '600'
          }}>
            Server Configuration
          </h3>
        </div>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '13px'
            }}>
              Connection Timeout (seconds)
            </label>
            <input
              type="number"
              min={30}
              max={600}
              value={values.connectionTimeout}
              onChange={e => handleInputChange('connectionTimeout', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '2px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div style={{
            background: '#ffffff',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '13px'
            }}>
              Max Connections
            </label>
            <input
              type="number"
              min={100}
              max={20000}
              value={values.maxConnections}
              onChange={e => handleInputChange('maxConnections', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '2px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div style={{
            background: '#ffffff',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '13px'
            }}>
              Max Threads
            </label>
            <input
              type="number"
              min={10}
              max={500}
              value={values.maxThreads}
              onChange={e => handleInputChange('maxThreads', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '2px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div style={{
            background: '#ffffff',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '13px'
            }}>
              Min Spare Threads
            </label>
            <input
              type="number"
              min={5}
              max={100}
              value={values.minSpareThreads}
              onChange={e => handleInputChange('minSpareThreads', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '2px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>
      </div>

      {/* Thread Pool Configuration */}
      <div style={{ 
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        borderRadius: '12px',
        border: '1px solid #fbbf24'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '20px',
          gap: '12px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            ⚡
          </div>
          <h3 style={{ 
            margin: 0, 
            color: '#1e293b',
            fontSize: '20px',
            fontWeight: '600'
          }}>
            Thread Pool Configuration
          </h3>
        </div>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '13px'
            }}>
              Core Pool Size
            </label>
            <input
              type="number"
              min={1}
              max={50}
              value={values.corePoolSize}
              onChange={e => handleInputChange('corePoolSize', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '2px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div style={{
            background: '#ffffff',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '13px'
            }}>
              Max Pool Size
            </label>
            <input
              type="number"
              min={1}
              max={100}
              value={values.maxPoolSize}
              onChange={e => handleInputChange('maxPoolSize', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '2px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div style={{
            background: '#ffffff',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '13px'
            }}>
              Queue Capacity
            </label>
            <input
              type="number"
              min={10}
              max={1000}
              value={values.queueCapacity}
              onChange={e => handleInputChange('queueCapacity', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '2px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div style={{
            background: '#ffffff',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '13px'
            }}>
              Keep Alive (seconds)
            </label>
            <input
              type="number"
              min={10}
              max={300}
              value={values.keepAliveSeconds}
              onChange={e => handleInputChange('keepAliveSeconds', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '2px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '32px',
        padding: '24px',
        background: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <button
          onClick={handleSave}
          disabled={isSaving}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: '#ffffff',
            border: 'none',
            padding: '16px 32px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            opacity: isSaving ? 0.7 : 1,
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            if (!isSaving) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
          }}
        >
          {isSaving ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #ffffff',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              Saving...
            </>
          ) : (
            <>
              💾 Save System Settings
            </>
          )}
        </button>
        
        {saveMessage && (
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            color: saveMessage.includes('successfully') ? '#065f46' : '#991b1b',
            background: saveMessage.includes('successfully') ? '#d1fae5' : '#fee2e2',
            border: `1px solid ${saveMessage.includes('successfully') ? '#a7f3d0' : '#fecaca'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            {saveMessage.includes('successfully') ? '✅' : '❌'} {saveMessage}
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default AdminSystemSettings;
